import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseAmount } from '../../../shared/model';

import {
  clampTipCheckoutAmount,
  TIP_CHECKOUT_FEEDBACK_MAX_LENGTH,
  TIP_CHECKOUT_FEEDBACK_PREFILL,
  formatTipCheckoutAmountValue,
  formatTipCheckoutCurrencyAmount,
  getTipCheckoutFeedbackPromptKey,
  isTipCheckoutPayableAmount,
  resolveTipCheckoutCountryCode,
  resolveTipCheckoutCurrencyPrefix,
  TIP_CHECKOUT_DEFAULT_AMOUNT,
  TIP_CHECKOUT_PRESET_AMOUNTS,
} from './tips-checkout.constants';

function formatManualAmountInputValue(amount: number | null) {
  return amount === null ? '' : String(amount);
}

function resolveInitialTipCheckoutAmount(initialAmount?: number) {
  const normalizedAmount =
    initialAmount === undefined ? null : clampTipCheckoutAmount(initialAmount);

  return normalizedAmount ?? TIP_CHECKOUT_DEFAULT_AMOUNT;
}

export function useTipCheckoutForm(
  feedbackPrefillText: string = TIP_CHECKOUT_FEEDBACK_PREFILL,
  initialAmount?: number,
) {
  const { i18n } = useTranslation();
  const countryCode = useMemo(
    () => resolveTipCheckoutCountryCode(i18n.language),
    [i18n.language],
  );
  const resolvedInitialAmount = resolveInitialTipCheckoutAmount(initialAmount);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(
    resolvedInitialAmount,
  );
  const [manualAmountInput, setManualAmountInput] = useState(
    String(resolvedInitialAmount),
  );
  const [isManualAmountEntry, setManualAmountEntry] = useState(false);
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [isFeedbackPrefilled, setIsFeedbackPrefilled] = useState(false);
  const [isConsentAccepted, setConsentAccepted] = useState(true);
  const [isFeeIncluded, setFeeIncluded] = useState(true);
  const selectedAmountRef = useRef(selectedAmount);
  const isManualAmountEntryRef = useRef(isManualAmountEntry);

  selectedAmountRef.current = selectedAmount;
  isManualAmountEntryRef.current = isManualAmountEntry;

  useEffect(() => {
    const isLocalManualAmountSync =
      isManualAmountEntryRef.current &&
      selectedAmountRef.current === resolvedInitialAmount;

    if (isLocalManualAmountSync) {
      return;
    }

    setSelectedAmount(resolvedInitialAmount);
    setManualAmountInput(String(resolvedInitialAmount));
    setManualAmountEntry(false);
  }, [resolvedInitialAmount]);

  const presetAmounts = useMemo(
    () =>
      TIP_CHECKOUT_PRESET_AMOUNTS.map(amount => ({
        amount,
        label: formatTipCheckoutCurrencyAmount(amount, countryCode),
      })),
    [countryCode],
  );

  const selectPresetAmount = (amount: number) => {
    const normalizedAmount = clampTipCheckoutAmount(amount);

    if (normalizedAmount === null) {
      return;
    }

    setSelectedAmount(normalizedAmount);
    setManualAmountInput(String(normalizedAmount));
    setManualAmountEntry(false);
  };

  const activateManualAmountEntry = () => {
    setManualAmountInput(formatManualAmountInputValue(selectedAmount));
    setManualAmountEntry(true);
  };

  const completeManualAmountEntry = () => {
    if (selectedAmount === null) {
      setSelectedAmount(TIP_CHECKOUT_DEFAULT_AMOUNT);
      setManualAmountInput(String(TIP_CHECKOUT_DEFAULT_AMOUNT));
      setManualAmountEntry(false);
      return;
    }

    setManualAmountInput(formatManualAmountInputValue(selectedAmount));
    setManualAmountEntry(false);
  };

  const updateManualAmount = (nextValue: string) => {
    const digitsOnly = nextValue.replace(/\D/g, '');

    setManualAmountInput(digitsOnly);

    const parsedAmount = parseAmount(digitsOnly);

    if (parsedAmount === null) {
      setSelectedAmount(parsedAmount);
      return;
    }

    const normalizedAmount = clampTipCheckoutAmount(parsedAmount);

    if (normalizedAmount === null) {
      setSelectedAmount(null);
      return;
    }

    if (normalizedAmount !== parsedAmount) {
      setManualAmountInput(String(normalizedAmount));
    }

    setSelectedAmount(normalizedAmount);
  };

  const selectRating = (ratingValue: number) => {
    setSelectedRating(ratingValue);

    if (!feedbackText) {
      setFeedbackText(feedbackPrefillText);
      setIsFeedbackPrefilled(true);
    }
  };

  const beginFeedbackEditing = () => {
    if (!isFeedbackPrefilled) {
      return;
    }

    setFeedbackText('');
    setIsFeedbackPrefilled(false);
  };

  const updateFeedbackText = (nextValue: string) => {
    const normalizedValue = nextValue.slice(
      0,
      TIP_CHECKOUT_FEEDBACK_MAX_LENGTH,
    );

    if (isFeedbackPrefilled) {
      setIsFeedbackPrefilled(false);
    }

    setFeedbackText(normalizedValue);
  };

  return {
    activateManualAmountEntry,
    canPay: isConsentAccepted && isTipCheckoutPayableAmount(selectedAmount),
    beginFeedbackEditing,
    completeManualAmountEntry,
    currencyPrefix: resolveTipCheckoutCurrencyPrefix(countryCode),
    feedbackCharacterCount: feedbackText.length,
    feedbackPromptKey: getTipCheckoutFeedbackPromptKey(selectedRating),
    feedbackText,
    isConsentAccepted,
    isFeeIncluded,
    isFeedbackPrefilled,
    isManualAmountEntry,
    manualAmountInput,
    presetAmounts,
    selectPresetAmount,
    selectedAmount,
    selectedAmountLabel:
      selectedAmount === null
        ? ''
        : formatTipCheckoutAmountValue(selectedAmount, countryCode),
    selectedRating,
    selectRating,
    setConsentAccepted,
    setFeeIncluded,
    shouldShowFeedbackField: selectedRating !== null,
    updateManualAmount,
    updateFeedbackText,
  };
}
